import { Injectable } from '@angular/core';
import { HttpService } from '../../core/services/http/http.service';
import {
  ChatAPIRequest,
  StartChatRequest,
  QueueQueryRequest,
  SendChatMessageRequest,
  ReconnectChatRequest,
  CreateCallbackRequest,
  PartyInfoRequest
} from '../../shared/models/chat-api-requests';
import { ChatEvent } from '../../shared/models/chat-api-responses';
import { ChatSession } from '../../shared/models/chat.model';
import { ChatMessages } from './chat-messages';
import { RequestHeaders, CacheConstant } from '../../core/constants/app.constant';
import { ParmedApiService as ParmedApi } from '../../core/api/parmed-api.service';
import { AppUtilService } from '../../core/utils/app-util.service';
import { ChatApiConstant } from '../../core/constants/parmed-api.constant';
import { Observable } from 'rxjs';
import { AppCacheService } from '../../core/services/cache/app-cache.service';
import { SanitizerService } from '../../core/services/sanitizer.service';
import { XhrFactory } from '@angular/common';
declare var $: any;

@Injectable()
export class ChatService {
  startChatRequest: StartChatRequest;
  chatAPIRequest: ChatAPIRequest;
  envProperties =  this.cache.get(CacheConstant.Env_Properties);
  constructor(
    private cache: AppCacheService,
    private messages: ChatMessages,
    private http: HttpService,
    private api: ParmedApi,
    private sanitizerService: SanitizerService,
    private xhr:XhrFactory
  ) {}

  start() {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return this.http.post(
      this.envProperties.chatApiUrl + ChatApiConstant.START_CHAT,
      JSON.stringify(this.startChatRequest),
      AppUtilService.getHttpDefaultOptions()
    );
  }

  queueQuery(queueQueryRequest: QueueQueryRequest) {
    return new Observable(observer => {
      const requestHeaders = {};
      requestHeaders[RequestHeaders.ContentType] = 'application/json';

      observer.next( $.ajax({
        type: 'POST',
        url: this.envProperties.chatApiUrl + ChatApiConstant.QUEUE_QUERY,
        contentType: 'application/json; charset=utf-8',
        async: false,
        data: JSON.stringify(queueQueryRequest),
        dataType: 'json',
        success: response => {
          return response;
        },
        error: function(xhr, error) {}
      }));
      
      observer.complete();
    });
  }

  poll(participantID: string) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return $.ajax({
      type: 'GET',
      url: this.envProperties.chatApiUrl + ChatApiConstant.POLL_CHAT + participantID,
      contentType: 'application/json; charset=utf-8',
      async: false,
      dataType: 'json',
      success: response => {
        return response;
      },
      error: (xhr, error) => {
        return xhr;
      }
    });
  }

  sendMessage(participantID: string, sendmessage: SendChatMessageRequest) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return $.ajax({
      type: 'POST',
      url:
        this.envProperties.chatApiUrl + ChatApiConstant.SEND_MESSAGE + participantID,
      contentType: 'application/json; charset=utf-8',
      async: false,
      data: JSON.stringify(sendmessage),
      dataType: 'json',
      success: response => {
        return response;
      },
      error: (xhr, error) => {}
    });
  }

  getPartyInfo(userParticipantID: string, repParticipantID: string) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    const partyInfoRequest: PartyInfoRequest = {
      participantID: repParticipantID
    };

    const sanitizedUrl = this.sanitizerService.sanitizeUrl(
      this.envProperties.chatApiUrl +
      ChatApiConstant.GET_PARTYINFO +
      userParticipantID
    );
    return this.http.post<any>(
      sanitizedUrl,
      JSON.stringify(partyInfoRequest),
      requestHeaders
    );
  }

  exit(participantID: string) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    const sanitizedUrl = this.sanitizerService.sanitizeUrl(
      this.envProperties.chatApiUrl + ChatApiConstant.EXIT_CHAT + participantID
    );
    return this.http.post<any>(
      sanitizedUrl,
      '',
      requestHeaders
    );
  }

  exitSync(participantID: string) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return $.ajax({
      type: 'POST',
      url:
        this.envProperties.chatApiUrl +
        ChatApiConstant.EXIT_CHAT +
        participantID,
      contentType: 'application/json; charset=utf-8',
      async: false,
      dataType: 'json',
      success: function(response) {
        console.log('chat ended');
        return response;
      },
      error: function(xhr, error) {}
    });
  }

  reconnect(reconnectChatRequest: ReconnectChatRequest) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return this.http.post<any>(
      this.envProperties.chatApiUrl + ChatApiConstant.RECONNECT_CHAT,
      JSON.stringify(reconnectChatRequest),
      requestHeaders
    );
  }

  setTypingState(typingIndicator: boolean, participantID: string) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    const data = {
      typingIndicator: typingIndicator
    };

    $.ajax({
      type: 'POST',
      url:
        this.envProperties.chatApiUrl +
        ChatApiConstant.SET_TYPING_STATE +
        participantID,
      contentType: 'application/json; charset=utf-8',
      async: true,
      data: JSON.stringify(data),
      dataType: 'json',
      success: function(response) {
        if (typingIndicator) {
          setTimeout(function() {
            this.setTypingState(participantID, false);
          }, 5000);
        }
      },
      error: function(xhr, error) {}
    });
  }

  handleEvents(
    events: Array<ChatEvent>,
    currentChatSession: ChatSession
  ): ChatSession {
    events.forEach((e: ChatEvent) => {
      if (e.type === 'text') {
        this.handleTextEvent(e, currentChatSession);
      } else if (e.type === 'participantStateChanged') {
        this.handleStateChangedEvent(e, currentChatSession);
      } else if (e.type === 'typingIndicator') {
        this.handleTypingIndicatorEvent(e, currentChatSession);
      }
    });
    return currentChatSession;
  }

  handleTextEvent(e: ChatEvent, currentChatSession: ChatSession): ChatSession {
    if (e.participantID === currentChatSession.userParticipantID) {
      currentChatSession.chatContent += this.messages.UserChatMessageTemplate.replace(
        '<<displayName>>',
        e.displayName
      )
        .replace('<<chatTime>>', this.getChatTime())
        .replace('<<message>>', e.value);
    } else {
      if (e.value.indexOf('transferred') > -1) {
        currentChatSession.isTransferEventReceived = true;
        currentChatSession.isDisconnectEventReceived = false;
      }
      currentChatSession.chatContent += this.messages.RepChatMessageTemplate.replace(
        '<<displayName>>',
        e.displayName
      )
        .replace('<<chatTime>>', this.getChatTime())
        .replace('<<message>>', e.value);
      if (e.participantID === currentChatSession.repParticipantID) {
        currentChatSession.lastRepChatMessage = e.value;
        currentChatSession.showRepInitAlert = true;
      }
    }

    return currentChatSession;
  }

  handleTypingIndicatorEvent(
    e: ChatEvent,
    currentChatSession: ChatSession
  ): ChatSession {
    if (e.participantID === currentChatSession.repParticipantID) {
      if (e.value.toString().toLowerCase() === 'true') {
        currentChatSession.typingIndicator =
          currentChatSession.repDisplayName + ' is typing...';
      } else if (e.value.toString().toLowerCase() === 'false') {
        currentChatSession.typingIndicator = '';
      }
    }
    return currentChatSession;
  }

  handleStateChangedEvent(
    e: ChatEvent,
    currentChatSession: ChatSession
  ): ChatSession {
    if (e.state.toLowerCase() === 'active') {
      switch (e.participantType.toLowerCase()) {
        case 'agent':
          currentChatSession.repParticipantID = e.participantID;
          currentChatSession.repParticipantName = e.participantName;
          currentChatSession.repDisplayName = e.participantName;
          break;
        case 'webuser':
          currentChatSession.userDisplayName = e.participantName;
          break;
        default:
          break;
      }
      if (currentChatSession.repDisplayName === e.participantName) {
        currentChatSession.repParticipantID = e.participantID;
      }
    } else if (e.state.toLowerCase() === 'disconnected') {
      currentChatSession.isDisconnectEventReceived = true;
    }
    return currentChatSession;
  }

  callBack(createCallbackRequest: CreateCallbackRequest) {
    const requestHeaders = {};
    requestHeaders[RequestHeaders.ContentType] = 'application/json';

    return this.http.post<any>(
      this.envProperties.chatApiUrl + ChatApiConstant.CREATE_CALLBACK,
      JSON.stringify(createCallbackRequest),
      requestHeaders
    );
  }

  getChatTime() {
    return new Date().toLocaleTimeString();
  }

  getGlobalSettings(globalChatSettingsData: any) {
    return this.http.post<any>(
      this.api.Config.GET_GLOBAL_CHAT_SETTINGS_URL,
      globalChatSettingsData,
      AppUtilService.getHttpDefaultOptions()
    );
  }
}
